import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import HomePage from './components/HomePage';
import MobileHomePage from './mobile/pages/MobileHomePage';
import MobileServicesPage from './mobile/pages/MobileServicesPageNew';
import MobilePacksPage from './mobile/pages/MobilePacksPage';
import MobileDevisRequestPage from './mobile/pages/MobileDevisRequestPage';
import MobileFactureProformaPage from './mobile/pages/MobileFactureProformaPage';
import MobileFacturesPage from './mobile/pages/MobileFacturesPage';
import MobileDemandesClientsPage from './mobile/pages/MobileDemandesClientsPage';
import MobilePromoWheelPage from './mobile/pages/MobilePromoWheelPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import AdminRegister from './pages/AdminRegister';
import MobileLoginPage from './components/mobile/MobileLoginPage';
import MobileRegisterPage from './components/mobile/MobileRegisterPage';
import DashboardAdmin from './components/DashboardAdmin';
import MobileDashboardAdmin from './components/mobile/MobileDashboardAdmin';
import MobileUserManager from './components/mobile/MobileUserManager';
import MobileGestionProjets from './components/mobile/MobileGestionProjets';
import MobileHistorique from './components/mobile/MobileHistorique';
import ServicesPage from './pages/ServicesPage';
import IAPage from './pages/IAPage';
import ContactPage from './pages/ContactPage';
import MessagesPage from './pages/MessagesPage';
import ProfilePage from './pages/ProfilePage';
import MesPromosPage from './pages/MesPromosPage';
import PromoWheelPage from './pages/PromoWheelPage';
import UsersPage from './pages/UsersPage';
import DevisRequestPage from './pages/DevisRequestPage';
import FactureProformaPage from './pages/FactureProformaPage';
import CompanyProfilePage from './pages/CompanyProfilePage';
import PurchaseRequestsPage from './pages/PurchaseRequestsPage';
import DetailsDemandeAchatPage from './pages/DetailsDemandeAchatPage';
import AboutIrisSoftechPage from './pages/AboutIrisSoftechPage';
import PacksPage from './pages/PacksPage';
import ClientInvoicesPage from './pages/ClientInvoicesPage';
import ProjectsPage from './pages/ProjectsPage';
import GestionProjetsPage from './pages/GestionProjetsPage';
import HistoriquePage from './pages/HistoriquePage';
import SubmitProjectPage from './pages/SubmitProjectPage';
import { BookingAppointmentPage } from './bookingappointment';
import { SimpleAuthProvider, useAuth } from './components/AuthProvider';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { useHeartbeat } from './hooks/useHeartbeat';
import { useDeviceDetection } from './hooks/useDeviceDetection';
import ChatbotPage from './chatbot/pages/ChatbotPage';
import { ChatbotProvider } from './chatbot/context/ChatbotContext';
import './backup-styles.css';

function AppContent() {
  // 🔐 État d'authentification
  const { isAuthenticated } = useAuth();
  
  // 🔥 Heartbeat actif uniquement si authentifié
  useHeartbeat({ isAuthenticated });
  
  // 📱 Détection mobile/desktop
  const { isMobile } = useDeviceDetection();

  return (
    <Routes>
      {/* Routes publiques avec Auth Guard (redirection si connecté) */}
      <Route path="/login" element={
        <PublicRoute>
          {isMobile ? <MobileLoginPage /> : <LoginPage />}
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          {isMobile ? <MobileRegisterPage /> : <RegisterPage />}
        </PublicRoute>
      } />
      <Route path="/admin/register" element={
        <PublicRoute>
          <AdminRegister />
        </PublicRoute>
      } />
      
      {/* Routes protégées */}
      <Route path="/" element={
        <ProtectedRoute requireAuth={false}>
          {isMobile ? <MobileHomePage /> : <HomePage />}
        </ProtectedRoute>
      } />
      
      {/* Page Services */}
      <Route path="/services" element={
        <ProtectedRoute requireAuth={false}>
          {isMobile ? <MobileServicesPage /> : <ServicesPage />}
        </ProtectedRoute>
      } />
      
      {/* Page Intelligence Artificielle */}
      <Route path="/ia" element={
        <ProtectedRoute requireAuth={false}>
          <IAPage />
        </ProtectedRoute>
      } />
      
      {/* Page Contact */}
      <Route path="/contact" element={
        <ProtectedRoute requireAuth={false}>
          <ContactPage />
        </ProtectedRoute>
      } />
      
      {/* Page Messages (Admin uniquement) */}
      <Route path="/messages" element={
        <ProtectedRoute requireAuth={true} requireAdmin={true}>
          <MessagesPage />
        </ProtectedRoute>
      } />
      
      {/* Page Profil Utilisateur */}
      <Route path="/profile" element={
        <ProtectedRoute requireAuth={true}>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Page Mes Promos */}
      <Route path="/mespromos" element={
        <ProtectedRoute requireAuth={true}>
          <MesPromosPage />
        </ProtectedRoute>
      } />
      
      {/* Page Roue de Promotions */}
      <Route path="/promowheel" element={
        <ProtectedRoute requireAuth={false}>
          {isMobile ? <MobilePromoWheelPage /> : <PromoWheelPage />}
        </ProtectedRoute>
      } />
      
      {/* Page Demande de Devis */}
      <Route path="/devisrequest" element={
        <ProtectedRoute requireAuth={true}>
          {isMobile ? <MobileDevisRequestPage /> : <DevisRequestPage />}
        </ProtectedRoute>
      } />
      
      {/* Page Facture Proforma */}
      <Route path="/facture-proforma" element={
        <ProtectedRoute requireAuth={false}>
          {isMobile ? <MobileFactureProformaPage /> : <FactureProformaPage />}
        </ProtectedRoute>
      } />
      
      {/* Page Factures Client */}
      <Route path="/factures" element={
        <ProtectedRoute requireAuth={true}>
          {isMobile ? <MobileFacturesPage /> : <ClientInvoicesPage />}
        </ProtectedRoute>
      } />
      
      {/* Page Projets */}
      <Route path="/projets" element={
        <ProtectedRoute requireAuth={true}>
          <ProjectsPage />
        </ProtectedRoute>
      } />
      
      {/* Page Gestion Projets (Admin uniquement) */}
      <Route path="/gestionprojets" element={
        <ProtectedRoute requireAuth={true} requireAdmin={true}>
          {isMobile ? (
            <MobileGestionProjets onClose={() => window.location.href = '/'} />
          ) : (
            <GestionProjetsPage />
          )}
        </ProtectedRoute>
      } />
      
      {/* Page Historique Projets (Admin uniquement) */}
      <Route path="/historique" element={
        <ProtectedRoute requireAuth={true} requireAdmin={true}>
          {isMobile ? (
            <MobileHistorique onClose={() => window.location.href = '/'} />
          ) : (
            <HistoriquePage />
          )}
        </ProtectedRoute>
      } />
      
      {/* Page À propos d'IRIS SOFTECH */}
      <Route path="/aboutirissoftech" element={
        <ProtectedRoute requireAuth={false}>
          <AboutIrisSoftechPage />
        </ProtectedRoute>
      } />
      
      {/* Page Packs */}
      <Route path="/packs" element={
        <ProtectedRoute requireAuth={false}>
          {isMobile ? <MobilePacksPage /> : <PacksPage />}
        </ProtectedRoute>
      } />
      
      {/* Page Réservation de Rendez-vous */}
      <Route path="/bookingappointment" element={
        <ProtectedRoute requireAuth={false}>
          <BookingAppointmentPage />
        </ProtectedRoute>
      } />
      
      {/* Page Soumission de Projet */}
      <Route path="/submitprojects" element={
        <ProtectedRoute requireAuth={true}>
          <SubmitProjectPage />
        </ProtectedRoute>
      } />
      
      {/* Routes admin */}
      <Route path="/admin" element={
        <ProtectedRoute requireAuth={true} requireAdmin={true}>
          {isMobile ? (
            <MobileDashboardAdmin onClose={() => window.location.href = '/'} />
          ) : (
            <DashboardAdmin onClose={() => window.location.href = '/'} />
          )}
        </ProtectedRoute>
      } />

      {/* Page Utilisateurs (Admin uniquement) */}
      <Route path="/users" element={
        <ProtectedRoute requireAuth={true} requireAdmin={true}>
          {isMobile ? (
            <MobileUserManager onClose={() => window.location.href = '/'} />
          ) : (
            <UsersPage />
          )}
        </ProtectedRoute>
      } />
      
      {/* Page Profil Entreprise (Admin uniquement) */}
      <Route path="/company-profile" element={
        <ProtectedRoute requireAuth={true} requireAdmin={true}>
          <CompanyProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Page Purchase Requests (Admin uniquement) */}
      <Route path="/demandesclients" element={
        <ProtectedRoute requireAuth={true} requireAdmin={true}>
          {isMobile ? <MobileDemandesClientsPage /> : <PurchaseRequestsPage />}
        </ProtectedRoute>
      } />
      
      {/* Page Détails Demande d'Achat (Admin uniquement) */}
      <Route path="/detailsdemande/:id" element={
        <ProtectedRoute requireAuth={true} requireAdmin={true}>
          <DetailsDemandeAchatPage />
        </ProtectedRoute>
      } />
      
      {/* Redirection pour les URLs incorrectes */}
      <Route path="/admin/login" element={<Navigate to="/login" replace />} />
      <Route path="/login_with_redirect.html" element={<Navigate to="/login" replace />} />
      
      {/* Route catch-all pour les URLs non définies */}
      <Route path="*" element={
        <ProtectedRoute requireAuth={false}>
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-gray-800 mb-4">404</h1>
              <h2 className="text-2xl font-semibold text-gray-700 mb-4">Page non trouvée</h2>
              <p className="text-gray-600 mb-8">La page que vous recherchez n'existe pas.</p>
              <button
                onClick={() => window.location.href = '/'}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <SimpleAuthProvider>
          <ChatbotProvider>
            <AppContent />
            {/* Chatbot IRIS IA - Disponible sur toutes les pages */}
            <ChatbotPage />
          </ChatbotProvider>
        </SimpleAuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
